// CandidateDetail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Bot, Building2, Calculator, CalendarClock, CalendarDays, Check,
  CheckCircle2, Circle, ClipboardCheck, ClipboardList, Download, FileText,
  ListChecks, Mail, Mic, PauseCircle, Pencil, Play, PlayCircle, RefreshCw, Search, ShieldAlert, Square, Target, Video, X,
  XCircle,
} from 'lucide-react';
import { candidatesApi, interviewsApi, jobsApi } from '../../../shared/services/api';
import { useAlert } from '../../../shared/contexts/AlertContext';
import { useAuth } from '../../auth';
import { canWorkJob } from '../../../shared/utils/permissions';
import TimeDropdowns, { formatTime12, hhmmToMinutes } from '../../../shared/components/TimeDropdowns';
import { ctcPlaceholder } from '../../../shared/utils/budget';
import {
  ExperienceTimeline, ProjectsList, EducationList, RequirementCoverage, ScoreCalculationModal, SkillCoverage, StrengthsAndRisks,
  breakdownLabel,
} from '../components/ProfileInsights';

const SCORE_COLOR = (s) => s >= 75 ? 'var(--green)' : s >= 55 ? 'var(--accent)' : s >= 35 ? 'var(--yellow)' : 'var(--red)';

function communicationFromBreakdown(breakdown) {
  const block = breakdown?.communication_skills || {};
  const named = String(block.level || '').trim();
  const score = Number(block.score);
  const max = Number(block.max) || 20;
  let level = ['Poor', 'Average', 'Fluent'].includes(named) ? named : '';
  if (!level && Number.isFinite(score)) {
    const pct = score / max;
    level = pct >= 0.7 ? 'Fluent' : pct >= 0.4 ? 'Average' : 'Poor';
  }
  if (!level) return null;
  return { level, score: Number.isFinite(score) ? score : null, max, notes: block.notes || '' };
}

function CommunicationBadge({ comm, size = 'md' }) {
  if (!comm) return null;
  const color = comm.level === 'Fluent' ? 'var(--green)' : comm.level === 'Average' ? 'var(--yellow)' : 'var(--red)';
  const big = size === 'lg';
  return (
    <div style={{
      textAlign: 'center',
      background: `${color}14`,
      border: `1px solid ${color}55`,
      borderRadius: 10,
      padding: big ? '10px 16px' : '8px 12px',
      minWidth: big ? 140 : 120,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
        Communication
      </div>
      <div style={{ fontSize: big ? 22 : 18, fontWeight: 800, color, lineHeight: 1.1 }}>{comm.level}</div>
      {comm.score != null && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{comm.score}/{comm.max}</div>
      )}
    </div>
  );
}

function emailDeliveryNote(result) {
  if (!result) return '';
  if (result.inbox_hint) return result.inbox_hint;
  if (result.email_sent) return 'Email sent to the candidate.';
  if (result.email_error) return `Email was not sent: ${result.email_error}`;
  return 'Email was not sent. Check SMTP settings.';
}

const SOURCE_COLORS = {
  LinkedIn: '#0a66c2',
  Indeed: '#2164f3',
  Naukri: '#ff7555',
  Monster: '#6d00cc',
  Upload: '#475569',
  Portal: '#059669',
  Email: '#d97706',
};

// ── Pipeline Stage Config ──────────────────────────────────────────────────
const PIPELINE_STAGES = [
  { key: 'applied',               label: 'Applied',           icon: FileText,      color: '#64748b' },
  { key: 'resume_scored',         label: 'Resume Scored',     icon: Search,        color: '#7c3aed' },
  { key: 'resume_reviewed',       label: 'Reviewed',          icon: ClipboardCheck, color: '#2563eb' },
  { key: 'interview_scheduled',   label: 'Scheduled',         icon: CalendarDays,  color: '#0891b2' },
  { key: 'interview_completed',   label: 'AI Interview',      icon: Bot,           color: '#059669' },
  { key: 'second_round_invited',  label: 'Next Round',        icon: Target,        color: '#d97706' },
  { key: 'second_round_scheduled',label: '2nd Round Set',     icon: CalendarClock, color: '#ea580c' },
  { key: 'second_round_completed',label: '2nd Round Done',    icon: ClipboardCheck, color: '#0d9488' },
  { key: 'completed',             label: 'Selected',          icon: CheckCircle2,  color: '#16a34a' },
];
const REJECTED_STAGES = ['rejected_resume', 'rejected_interview', 'rejected_final'];

/**
 * Stages the backend writes that are not steps of their own.
 *
 * Without this a candidate mid-interview, or one carrying the retired
 * `interview_invited`, matched nothing in the list — findIndex returned -1 and
 * every step rendered as "not reached", so the bar read as though they had
 * never applied.
 */
const STAGE_ALIASES = {
  interview_invited: 'resume_reviewed',     // retired: scoring no longer invites
  interview_canceled: 'resume_reviewed',    // retired: cancelling returns to Reviewed
  // Sitting the interview is what the AI Interview step marks, so it lights up
  // when the candidate starts. Waiting for a clean finish left anyone who
  // closed the tab mid-interview showing as merely Scheduled, even with their
  // recordings already on the server.
  interview_in_progress: 'interview_completed',
};

function PipelineProgress({ stage, emailSent, reason }) {
  const isRejected = REJECTED_STAGES.includes(stage);
  const resolved = STAGE_ALIASES[stage] || stage;
  const currentIdx = PIPELINE_STAGES.findIndex(s => s.key === resolved);

  if (isRejected) {
    const label = stage === 'rejected_resume' ? 'Resume Rejected'
      : stage === 'rejected_final' ? 'Not Selected'
      : 'Interview Rejected';
    return (
      <div style={{
        background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)',
        borderRadius: 10, padding: '12px 18px', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', color: 'var(--red)' }}><XCircle size={22} strokeWidth={1.9} /></span>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--red)', fontSize: 14 }}>{label}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {emailSent
              ? 'A rejection email has been sent to the candidate.'
              : 'No rejection email has been sent — the candidate has not been told.'}
            {reason ? ` Reason: ${reason}` : ''}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '14px 20px', marginBottom: 16,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Pipeline Progress
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 4 }}>
        {PIPELINE_STAGES.map((s, i) => {
          const isDone = i < currentIdx;
          const isCurrent = i === currentIdx;
          const isFuture = i > currentIdx;
          return (
            <React.Fragment key={s.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: isDone ? 'var(--green)' : isCurrent ? s.color : 'var(--bg-hover)',
                  border: `2px solid ${isDone ? 'var(--green)' : isCurrent ? s.color : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isDone ? 14 : 16,
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  color: isDone || isCurrent ? '#fff' : 'var(--text-muted)',
                }}>
                  {isDone ? <Check size={15} strokeWidth={2.6} /> : <s.icon size={15} strokeWidth={2} />}
                </div>
                <div style={{
                  fontSize: 10, marginTop: 5, textAlign: 'center', fontWeight: isCurrent ? 700 : 400,
                  color: isDone ? 'var(--green)' : isCurrent ? s.color : 'var(--text-muted)',
                  maxWidth: 70, lineHeight: 1.3,
                }}>
                  {s.label}
                </div>
              </div>
              {i < PIPELINE_STAGES.length - 1 && (
                <div style={{
                  flex: 1, height: 2, minWidth: 8,
                  background: isDone ? 'var(--green)' : 'var(--border)',
                  marginBottom: 18,
                  transition: 'background 0.2s',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Second Round Modal ─────────────────────────────────────────────────────
const MONTHS_SR = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_SR   = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const pad2 = n => String(n).padStart(2, '0');
const localDateStr = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const toOffsetIso = (localValue) => {
  if (!localValue) return localValue;
  const [datePart, timePart = '00:00'] = localValue.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = (timePart || '00:00').split(':').map(Number);
  const dt = new Date(y, (mo || 1) - 1, d || 1, h || 0, mi || 0, 0);
  const off = -dt.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  return `${datePart}T${pad2(h || 0)}:${pad2(mi || 0)}:00${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
};
const isLocalDateTimePast = (localValue) => {
  if (!localValue) return false;
  const [datePart, timePart = '00:00'] = localValue.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  return new Date(y, (mo || 1) - 1, d || 1, h || 0, mi || 0, 0).getTime() <= Date.now();
};
const nextFutureLocal = () => {
  const d = new Date(Date.now() + 60 * 1000);
  d.setSeconds(0, 0);
  d.setMilliseconds(0);
  const extra = (5 - (d.getMinutes() % 5)) % 5;
  d.setMinutes(d.getMinutes() + extra);
  return { dateStr: localDateStr(d), hhmm: `${pad2(d.getHours())}:${pad2(d.getMinutes())}` };
};
const PAST_SLOT_ALERT = {
  type: 'warning',
  title: 'Pick a future time',
  message: 'The interview cannot be scheduled in the past. Choose a date and time after now.',
};

function DateTimePicker({ value, onChange }) {
  const next = nextFutureLocal();
  const minY = Number(next.dateStr.slice(0, 4));
  const minM = Number(next.dateStr.slice(5, 7)) - 1;
  const [viewYear,  setViewYear]  = useState(() => minY);
  const [viewMonth, setViewMonth] = useState(() => minM);
  const [selDate,   setSelDate]   = useState(value ? value.slice(0, 10) : null);
  const [selTime,   setSelTime]   = useState(value ? value.slice(11, 16) : '10:00');

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const prevDisabled = viewYear < minY || (viewYear === minY && viewMonth <= minM);
  const minHhmm = selDate === next.dateStr ? next.hhmm : null;

  const clampTime = (dateStr, time) => {
    if (dateStr !== next.dateStr) return time;
    if (hhmmToMinutes(time) < hhmmToMinutes(next.hhmm)) return next.hhmm;
    return time;
  };

  const prevM = () => {
    if (prevDisabled) return;
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextM = () => viewMonth === 11 ? (setViewYear(y => y + 1), setViewMonth(0)) : setViewMonth(m => m + 1);

  const pick = (dateStr, time) => {
    const d = dateStr || selDate;
    const t = clampTime(d, time || selTime);
    if (d && t) onChange(`${d}T${t}`);
  };

  return (
    <div className="r-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {/* Calendar */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <button
            onClick={prevM}
            disabled={prevDisabled}
            style={{
              background: 'none', border: 'none',
              cursor: prevDisabled ? 'not-allowed' : 'pointer',
              color: 'var(--text-secondary)', fontSize: 18, padding: '0 4px',
              opacity: prevDisabled ? 0.35 : 1,
            }}
          >‹</button>
          <span style={{ fontWeight: 700, fontSize: 12.5 }}>{MONTHS_SR[viewMonth]} {viewYear}</span>
          <button onClick={nextM} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 18, padding: '0 4px' }}>›</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 3 }}>
          {DAYS_SR.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', padding: '2px 0' }}>{d}</div>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day     = i + 1;
            const dateStr = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const isPast  = dateStr < next.dateStr;
            const isSel   = selDate === dateStr;
            const isToday = dateStr === localDateStr();
            return (
              <button
                key={day}
                disabled={isPast}
                onClick={() => {
                  const t = clampTime(dateStr, selTime);
                  setSelDate(dateStr);
                  setSelTime(t);
                  pick(dateStr, t);
                }}
                style={{
                  padding: '6px 0', borderRadius: 5, fontSize: 12, fontWeight: isSel || isToday ? 700 : 400,
                  border: isSel ? '2px solid var(--accent)' : isToday ? '1px solid rgba(88,166,255,0.5)' : '1px solid transparent',
                  background: isSel ? 'var(--accent)' : 'transparent',
                  color: isSel ? '#fff' : isPast ? 'var(--text-muted)' : 'var(--text-primary)',
                  cursor: isPast ? 'not-allowed' : 'pointer', opacity: isPast ? 0.35 : 1,
                }}
              >{day}</button>
            );
          })}
        </div>
      </div>

      {/* Time */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {selDate ? (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>
              {new Date(selDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
            <TimeDropdowns
              value={selTime}
              minHhmm={minHhmm}
              onChange={(t) => {
                const clamped = clampTime(selDate, t);
                setSelTime(clamped);
                pick(selDate, clamped);
              }}
            />
            <div style={{ marginTop: 12, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              {formatTime12(selTime)}
            </div>
            {minHhmm && (
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                Past times cannot be scheduled.
              </div>
            )}
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', lineHeight: 1.7 }}>
            <ArrowLeft size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Pick a date<br />first
          </div>
        )}
      </div>
    </div>
  );
}

function SecondRoundModal({ candidate, jobTitle, defaults, onClose, onScheduled }) {
  const { showAlert } = useAlert();
  const [slot, setSlot] = useState('');
  const [meetingType, setMeetingType] = useState(
    defaults?.platform === 'face_to_face' ? 'face_to_face' : 'online',
  );
  const [meetingLink, setMeetingLink] = useState(defaults?.meeting_link || '');
  const [location, setLocation] = useState(defaults?.location || '');
  const [scheduling, setScheduling] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setMeetingType(defaults?.platform === 'face_to_face' ? 'face_to_face' : 'online');
    setMeetingLink(defaults?.meeting_link || '');
    setLocation(defaults?.location || '');
  }, [defaults]);

  const handleSchedule = async () => {
    if (!slot) return showAlert({ type: 'warning', title: 'Date & time required', message: 'Please enter a date and time for the second round.' });
    if (isLocalDateTimePast(slot)) return showAlert(PAST_SLOT_ALERT);
    if (meetingType === 'face_to_face' && !location.trim()) {
      return showAlert({ type: 'warning', title: 'Location required', message: 'Please enter the interview location or address.' });
    }
    if (meetingType !== 'face_to_face') {
      const link = meetingLink.trim();
      if (!link) {
        return showAlert({ type: 'warning', title: 'Meeting link required', message: 'Create the meeting in Teams, Zoom or Google Meet and paste the join link here.' });
      }
      if (!/^https?:\/\/\S+$/i.test(link)) {
        return showAlert({ type: 'warning', title: 'Check the meeting link', message: 'Paste the full join link, starting with https://' });
      }
    }
    setScheduling(true);
    try {
      const data = await interviewsApi.scheduleSecondRound(
        candidate.id, toOffsetIso(slot), meetingType, meetingLink, location
      );
      setResult(data);
      onScheduled(data);
    } catch (e) {
      showAlert({ type: 'error', title: 'Scheduling failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setScheduling(false);
    }
  };

  const MEETING_TYPES = [
    { value: 'online', label: 'Online', Icon: Video },
    { value: 'face_to_face', label: 'In-Person / Face-to-Face', Icon: Building2 },
  ];



  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 1000, overflowY: 'auto', padding: '24px 16px',
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 16, padding: '24px 28px',
        width: '100%', maxWidth: 540, border: '1px solid var(--border)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        margin: 'auto',
        position: 'relative',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>Schedule Second Round</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Human / Technical interview — {candidate.name}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}><X size={20} strokeWidth={2} /></button>
        </div>

        {!result ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Date & Time</label>
              <DateTimePicker value={slot} onChange={setSlot} />
              {slot && (
                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: isLocalDateTimePast(slot) ? 'var(--red)' : 'var(--green)' }}>
                  {isLocalDateTimePast(slot)
                    ? 'This time is in the past. Pick a later slot.'
                    : <><Check size={12} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px' }} /> {new Date(slot).toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Interview Format</label>
              <div className="r-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {MEETING_TYPES.map(t => (
                  <div
                    key={t.value}
                    onClick={() => setMeetingType(t.value)}
                    style={{
                      padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                      border: `2px solid ${meetingType === t.value ? 'var(--accent)' : 'var(--border)'}`,
                      background: meetingType === t.value ? 'rgba(37,99,235,0.08)' : 'var(--bg-surface)',
                      color: meetingType === t.value ? 'var(--accent)' : 'var(--text-secondary)',
                      fontWeight: meetingType === t.value ? 600 : 400,
                      transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', gap: 7,
                    }}
                  >
                    <t.Icon size={14} strokeWidth={2} />
                    {t.label}
                  </div>
                ))}
              </div>
            </div>

            {meetingType === 'face_to_face' ? (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Location / Address</label>
                <input
                  type="text"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="e.g. 123 Main St, Floor 4, Conference Room B"
                  style={{
                    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                    background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    borderRadius: 8, color: 'var(--text-primary)', fontSize: 14,
                  }}
                />
              </div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                  Meeting Link <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="url"
                  value={meetingLink}
                  onChange={e => setMeetingLink(e.target.value)}
                  placeholder="https://..."
                  style={{
                    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                    background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    borderRadius: 8, color: 'var(--text-primary)', fontSize: 14,
                  }}
                />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Create the meeting in Teams, Zoom or Google Meet yourself, then
                  paste the join link here. It goes to the candidate exactly as you
                  paste it.
                </div>
              </div>
            )}

            <div style={{
              background: 'rgba(5,150,105,0.07)', border: '1px solid var(--green)',
              borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: 'var(--text-secondary)',
              marginBottom: 20, lineHeight: 1.6,
            }}>
              <strong style={{ color: 'var(--green)' }}><Check size={13} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px' }} /> Candidate will be emailed</strong> — the slot, and the
              link or address you entered above, are sent on scheduling.
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSchedule} disabled={scheduling || isLocalDateTimePast(slot)} style={{ flex: 2 }}>
                {scheduling ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Scheduling…</> : <><CalendarClock size={14} strokeWidth={2} /> Schedule Second Round</>}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid var(--green)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Second Round Scheduled!</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <strong>Date & Time:</strong> {result.slot || slot}<br />
                <strong>Format:</strong> {MEETING_TYPES.find(t => t.value === result.meeting_type)?.label || result.meeting_type}<br />
                {result.location && <><strong>Location:</strong> {result.location}<br /></>}
                {result.meeting_link && <><strong>Link:</strong> {result.meeting_link}<br /></>}
              </div>
            </div>
            <div style={{ fontSize: 12, color: result.email_sent ? 'var(--green)' : 'var(--yellow)', marginBottom: 16, lineHeight: 1.5 }}>
              {emailDeliveryNote(result)}
            </div>
            {result.email_preview && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>
                  Email Preview
                </div>
                <pre style={{
                  background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8,
                  padding: '10px 14px', fontSize: 11.5, color: 'var(--text-secondary)',
                  whiteSpace: 'pre-wrap', fontFamily: 'inherit', maxHeight: 180, overflowY: 'auto'
                }}>{result.email_preview}</pre>
              </div>
            )}
            <button className="btn btn-primary" onClick={onClose} style={{ width: '100%' }}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Schedule Modal ─────────────────────────────────────────────────────────
function ScheduleModal({ candidate, jobTitle, onClose, onScheduled }) {
  const { showAlert } = useAlert();
  const [slot, setSlot] = useState('');
  const [duration, setDuration] = useState(30);
  const [scheduling, setScheduling] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSchedule = async () => {
    if (!slot) return showAlert({ type: 'warning', title: 'Date & time required', message: 'Please select a date and time.' });
    if (isLocalDateTimePast(slot)) return showAlert(PAST_SLOT_ALERT);
    setScheduling(true);
    try {
      const data = await interviewsApi.scheduleInterview(candidate.id, toOffsetIso(slot), duration);
      setResult(data);
      onScheduled(data);
    } catch (e) {
      showAlert({ type: 'error', title: 'Scheduling failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setScheduling(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 1000, overflowY: 'auto', padding: '24px 16px',
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 16, padding: 32,
        width: '100%', maxWidth: 560, border: '1px solid var(--border)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        margin: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>Schedule AI Interview</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Portal meeting room — {candidate.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}><X size={20} strokeWidth={2} /></button>
        </div>

        {!result ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Interview Date & Time</label>
              <DateTimePicker value={slot} onChange={setSlot} />
              {slot && (
                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: isLocalDateTimePast(slot) ? 'var(--red)' : 'var(--green)' }}>
                  {isLocalDateTimePast(slot)
                    ? 'This time is in the past. Pick a later slot.'
                    : <><Check size={12} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px' }} /> {new Date(slot).toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} IST</>}
                </div>
              )}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Duration</label>
              <select
                value={duration}
                onChange={e => setDuration(Number(e.target.value))}
                style={{
                  width: '100%', padding: '10px 12px',
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: 8, color: 'var(--text-primary)', fontSize: 14,
                }}
              >
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={20}>20 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
                Spoken questions follow this duration (15-min vs 30-min mix). Coding is extra — 10 minutes on top, so 15 shows as 25 and 30 shows as 40 on the candidate timer. The room stays open for 15 minutes after the scheduled start; later than that it closes. A late join still starts from question 1.
              </div>
            </div>
            <div style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '12px 14px', fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>Position:</strong> {jobTitle}<br />
              <strong style={{ color: 'var(--text-primary)' }}>Candidate:</strong> {candidate.name} ({candidate.email})<br />
              <strong style={{ color: 'var(--text-primary)' }}>Resume Score:</strong> {candidate.resume_score}/100
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSchedule} disabled={scheduling || isLocalDateTimePast(slot)} style={{ flex: 2 }}>
                {scheduling ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Creating room…</> : 'Create Meeting Room & Schedule'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid var(--green)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Interview Scheduled Successfully!</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <strong>Slot:</strong> {result.slot}<br />
                <strong>Duration:</strong> {result.duration_min} minutes<br />
                <strong>Room code:</strong> <span style={{ fontFamily: 'var(--font-mono)' }}>{result.room_code || result.teams_meeting_id}</span>
              </div>
              <div style={{ fontSize: 12.5, color: result.email_sent ? 'var(--green)' : 'var(--yellow)', marginTop: 10, lineHeight: 1.5 }}>
                {emailDeliveryNote(result)}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Portal Meeting Link</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  readOnly value={result.meeting_link || result.teams_meeting_link}
                  style={{ flex: 1, padding: '8px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 12, fontFamily: 'var(--font-mono)' }}
                />
                <button className="btn btn-secondary btn-sm" onClick={() => copy(result.meeting_link || result.teams_meeting_link)}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Email Preview for Candidate</div>
              <pre style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8,
                padding: '10px 14px', fontSize: 11.5, color: 'var(--text-secondary)',
                whiteSpace: 'pre-wrap', fontFamily: 'inherit', maxHeight: 140, overflowY: 'auto'
              }}>{result.email_preview}</pre>
            </div>
            <button className="btn btn-primary" onClick={onClose} style={{ width: '100%' }}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Linked Transcript (SOW 6.7) ────────────────────────────────────────────
// Renders interview transcript with inline ▶ Watch buttons that expand a
// video player right below the candidate's answer for that question.
function LinkedTranscript({ transcript, videos, candidateId, inlineVideo, setInlineVideo }) {
  // Build lookup: question_id → video filename
  const videoMap = React.useMemo(() => {
    const map = {};
    (videos || []).forEach(v => {
      if (v.question_id) map[v.question_id] = v.filename;
    });
    return map;
  }, [videos]);

  const filtered = transcript.filter(m => m.type !== 'intro' && m.type !== 'closing');

  // Group messages into Q&A pairs so we can attach video to the answer
  // Pair = { question: msg, answer: msg|null }
  const pairs = [];
  let pending = null;
  filtered.forEach(m => {
    if (m.role === 'interviewer') {
      if (pending) pairs.push(pending);
      pending = { question: m, answer: null };
    } else if (m.role === 'candidate' && pending) {
      pending.answer = m;
      pairs.push(pending);
      pending = null;
    } else {
      // standalone candidate message (unlikely but safe)
      pairs.push({ question: null, answer: m });
    }
  });
  if (pending) pairs.push(pending);

  const BASE_URL = process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {pairs.map((pair, i) => {
        const qId = pair.question?.question_id || pair.answer?.question_id;
        const videoFilename = qId ? videoMap[qId] : null;
        const isExpanded = inlineVideo === videoFilename && videoFilename;
        const videoUrl = videoFilename ? `${BASE_URL}/interviews/${candidateId}/video/${videoFilename}` : null;

        return (
          <div key={i} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 14, marginBottom: 14 }}>
            {/* Interviewer question */}
            {pair.question && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{
                  fontSize: 11, color: 'var(--accent)', fontWeight: 700,
                  minWidth: 90, paddingTop: 2, flexShrink: 0,
                }}>
                  AI Interviewer
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1, lineHeight: 1.6 }}>
                  {pair.question.content}
                </div>
              </div>
            )}

            {/* Candidate answer */}
            {pair.answer && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{
                  fontSize: 11, color: 'var(--green)', fontWeight: 700,
                  minWidth: 90, paddingTop: 2, flexShrink: 0,
                }}>
                  Candidate
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: videoFilename ? 8 : 0 }}>
                    {pair.answer.content}
                  </div>

                  {/* ── Video clip link (SOW 6.7) ── */}
                  {videoFilename && (
                    <div>
                      <button
                        onClick={() => setInlineVideo(isExpanded ? null : videoFilename)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: isExpanded ? 'rgba(37,99,235,0.12)' : 'var(--bg-hover)',
                          border: `1px solid ${isExpanded ? 'var(--accent)' : 'var(--border)'}`,
                          borderRadius: 6, padding: '4px 10px',
                          fontSize: 12, fontWeight: 600,
                          color: isExpanded ? 'var(--accent)' : 'var(--text-secondary)',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center' }}>{isExpanded ? <Square size={13} strokeWidth={2.2} /> : <Play size={13} strokeWidth={2.2} />}</span>
                        {isExpanded ? 'Hide Recording' : 'Watch Recording'}
                      </button>

                      {/* Inline player */}
                      {isExpanded && (
                        <div style={{
                          marginTop: 10, borderRadius: 10, overflow: 'hidden',
                          border: '1px solid var(--accent)',
                          background: '#080808',
                          maxWidth: 560,
                        }}>
                          <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '6px 12px', background: 'rgba(37,99,235,0.08)',
                            borderBottom: '1px solid var(--accent)',
                          }}>
                            <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>
                              Recording — {qId?.toUpperCase() || 'Answer'}
                            </span>
                            <a
                              href={videoUrl}
                              download={videoFilename}
                              style={{ fontSize: 11, color: 'var(--text-muted)', textDecoration: 'none' }}
                            >
                              <Download size={11} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-1px' }} /> Download
                            </a>
                          </div>
                          <video
                            key={videoFilename}
                            controls
                            autoPlay
                            style={{ width: '100%', display: 'block', maxHeight: 280, objectFit: 'contain' }}
                            src={videoUrl}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function NextRoundConfirmModal({ name, onClose, onConfirm, busy }) {
  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose} role="presentation">
      <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="next-round-confirm-title">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Target size={22} color="var(--accent)" strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <h3 id="next-round-confirm-title">Select for next round?</h3>
            <p className="modal-sub" style={{ marginBottom: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {name ? `${name} will be moved to the second round.` : 'This candidate will be moved to the second round.'}
              {' '}You can then schedule the meeting. Reject and On hold will no longer be available here.
            </p>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Selecting…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

function cityOnly(value) {
  return String(value || '').split(',')[0].trim();
}

const EDIT_PROFILE_FIELD_STYLE = {
  width: '100%', padding: '10px 12px', boxSizing: 'border-box',
  background: 'var(--bg-surface)', border: '1px solid var(--border)',
  borderRadius: 8, color: 'var(--text-primary)', fontSize: 13,
};

function EditProfileField({ id, label, children, wide }) {
  return (
    <div style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <label htmlFor={id} className="form-label" style={{ marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

function EditProfileModal({ candidate, onClose, onSave, busy, currency }) {
  const [form, setForm] = useState({
    name: candidate?.name || '',
    email: candidate?.email || '',
    phone: candidate?.phone || '',
    current_ctc: candidate?.current_ctc || '',
    expected_ctc: candidate?.expected_ctc || '',
    work_mode: candidate?.work_mode || '',
    location: cityOnly(candidate?.location),
  });

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      current_ctc: form.current_ctc.trim(),
      expected_ctc: form.expected_ctc.trim(),
      work_mode: form.work_mode,
      location: cityOnly(form.location),
    });
  };

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose} role="presentation">
      <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="edit-profile-title" style={{ maxWidth: 520, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
          <div>
            <h3 id="edit-profile-title" style={{ margin: 0 }}>Edit profile</h3>
            <p className="modal-sub" style={{ margin: '6px 0 0' }}>Update contact, CTC and work preferences for this candidate.</p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy} aria-label="Close" style={{ padding: 6 }}>
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="r-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
            <EditProfileField id="edit-name" label="Name">
              <input id="edit-name" className="form-input" style={EDIT_PROFILE_FIELD_STYLE} value={form.name} onChange={e => update('name', e.target.value)} disabled={busy} required />
            </EditProfileField>
            <EditProfileField id="edit-email" label="Email">
              <input id="edit-email" type="email" className="form-input" style={EDIT_PROFILE_FIELD_STYLE} value={form.email} onChange={e => update('email', e.target.value)} disabled={busy} />
            </EditProfileField>
            <EditProfileField id="edit-phone" label="Phone">
              <input id="edit-phone" className="form-input" style={EDIT_PROFILE_FIELD_STYLE} value={form.phone} onChange={e => update('phone', e.target.value)} disabled={busy} />
            </EditProfileField>
            <EditProfileField id="edit-work-mode" label="Work mode">
              <select id="edit-work-mode" className="form-select" style={EDIT_PROFILE_FIELD_STYLE} value={form.work_mode} onChange={e => update('work_mode', e.target.value)} disabled={busy}>
                <option value="">Not specified</option>
                <option value="On-site">On-site</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </EditProfileField>
            <EditProfileField id="edit-current-ctc" label="Current CTC">
              <input id="edit-current-ctc" className="form-input" style={EDIT_PROFILE_FIELD_STYLE} value={form.current_ctc} onChange={e => update('current_ctc', e.target.value)} disabled={busy} placeholder={ctcPlaceholder(currency, 'current')} />
            </EditProfileField>
            <EditProfileField id="edit-expected-ctc" label="Expected CTC">
              <input id="edit-expected-ctc" className="form-input" style={EDIT_PROFILE_FIELD_STYLE} value={form.expected_ctc} onChange={e => update('expected_ctc', e.target.value)} disabled={busy} placeholder={ctcPlaceholder(currency, 'expected')} />
            </EditProfileField>
            <EditProfileField id="edit-location" label="Preferred location (city)" wide>
              <input id="edit-location" className="form-input" style={EDIT_PROFILE_FIELD_STYLE} value={form.location} onChange={e => update('location', e.target.value)} disabled={busy} placeholder="e.g. Bengaluru" />
            </EditProfileField>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !form.name.trim()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HoldReasonModal({ name, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose} role="presentation">
      <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="hold-reason-title">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <PauseCircle size={22} color="var(--yellow)" strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 id="hold-reason-title">Put on hold?</h3>
            <p className="modal-sub" style={{ marginBottom: 12, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {name ? `${name} will be moved to On hold.` : 'This candidate will be moved to On hold.'}
              {' '}You can still shortlist or reject them later.
            </p>
            <label htmlFor="hold-reason" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
              Reason (optional)
            </label>
            <textarea
              id="hold-reason"
              placeholder="Why is this candidate on hold?"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              disabled={busy}
              style={{
                width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-primary)', fontSize: 13,
                resize: 'vertical',
              }}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onConfirm(reason.trim())} disabled={busy}>
            {busy ? 'Saving…' : 'Put on hold'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectReasonModal({ name, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose} role="presentation">
      <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="reject-reason-title">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <XCircle size={22} color="var(--red)" strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 id="reject-reason-title">Reject this candidate?</h3>
            <p className="modal-sub" style={{ marginBottom: 12, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {name ? `${name} will be marked as rejected.` : 'This candidate will be marked as rejected.'}
            </p>
            <label htmlFor="reject-reason" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
              Reason (optional)
            </label>
            <textarea
              id="reject-reason"
              placeholder="Why is this candidate being rejected?"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              disabled={busy}
              style={{
                width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-primary)', fontSize: 13,
                resize: 'vertical',
              }}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onConfirm(reason.trim())}
            disabled={busy}
            style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
          >
            {busy ? 'Saving…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelInterviewModal({
  onClose, onConfirm, busy,
  title = 'Cancel this interview?',
  message = 'This scheduled AI interview will be canceled, and a cancellation email will be sent to the candidate. You can schedule a new AI interview after canceling.',
  confirmLabel = 'Cancel interview',
  keepLabel = 'Keep interview',
}) {
  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose} role="presentation">
      <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="cancel-interview-title">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <ShieldAlert size={22} color="var(--red)" strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <h3 id="cancel-interview-title">{title}</h3>
            <p className="modal-sub" style={{ marginBottom: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {message}
            </p>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {keepLabel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={busy}
            style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
          >
            {busy ? 'Canceling…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 2nd Round Outcome Modal ────────────────────────────────────────────────
// Marks the 2nd round done (with optional feedback), or edits it afterwards:
// flip it back to not done, or change the feedback.
function SecondRoundOutcomeModal({ candidate, mode, onClose, onSaved }) {
  const { showAlert } = useAlert();
  const decided = ['completed', 'rejected_final'].includes(candidate.pipeline_stage);
  const [done, setDone] = useState(mode === 'done' || candidate.pipeline_stage !== 'second_round_scheduled');
  const [feedback, setFeedback] = useState(candidate.second_round_feedback || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await interviewsApi.updateSecondRoundOutcome(candidate.id, done, feedback);
      onSaved();
    } catch (e) {
      showAlert({ type: 'error', title: 'Could not save', message: e.message || 'Something went wrong. Please try again.' });
      setSaving(false);
    }
  };

  const option = (value, label, sub, disabled) => (
    <label style={{
      flex: '1 1 160px', display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px',
      borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
      border: `1px solid ${done === value ? 'var(--accent)' : 'var(--border)'}`,
      background: done === value ? 'var(--accent-glow)' : 'var(--bg-surface)',
    }}>
      <input type="radio" name="second-round-status" checked={done === value} disabled={disabled}
        onChange={() => setDone(value)} style={{ marginTop: 3, accentColor: 'var(--accent)' }} />
      <span>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{label}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</span>
      </span>
    </label>
  );

  return (
    <div className="modal-backdrop" onClick={saving ? undefined : onClose} role="presentation">
      <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="second-round-outcome-title">
        <h3 id="second-round-outcome-title">{mode === 'done' ? 'Mark 2nd round as done' : 'Edit 2nd round'}</h3>
        <p className="modal-sub">
          {candidate.name || 'Candidate'}{candidate.second_round_slot ? ` · ${candidate.second_round_slot}` : ''}
        </p>

        {mode === 'edit' && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Status</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: decided ? 6 : 16 }}>
              {option(true, 'Done', 'The 2nd round took place', false)}
              {option(false, 'Not done yet', 'Back to scheduled', decided)}
            </div>
            {decided && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 16 }}>
                The candidate is already selected or rejected, so only the feedback can change.
              </div>
            )}
          </>
        )}

        <label htmlFor="second-round-feedback" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
          Feedback <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
        </label>
        <textarea
          id="second-round-feedback"
          className="form-input"
          rows={4}
          value={feedback}
          onChange={e => setFeedback(e.target.value)}
          placeholder="How did the 2nd round go?"
          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
          disabled={saving || (mode === 'edit' && !done)}
        />

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : mode === 'done' ? 'Mark as done' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 2nd Round Card ─────────────────────────────────────────────────────────
// Everything about the 2nd round in one place, and the way to change it.
function SecondRoundCard({ c, due, onMarkDone, canEdit = true }) {
  // Accepted or rejected in the final round means the round happened, even for
  // candidates decided before it could be marked done.
  const done = ['second_round_completed', 'completed', 'rejected_final'].includes(c.pipeline_stage);
  const status = done
    ? { label: 'Done', color: 'var(--green)', bg: 'rgba(5,150,105,0.1)' }
    : due
      ? { label: 'Time passed, not marked done', color: '#ea580c', bg: 'rgba(234,88,12,0.1)' }
      : { label: 'Scheduled', color: 'var(--accent)', bg: 'var(--accent-glow)' };
  const online = c.second_round_type !== 'face_to_face';
  const row = (label, value) => (
    <div className="r-row-label" style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <div style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ color: 'var(--text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div className="section-title" style={{ marginBottom: 0 }}>2nd Round</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: status.color, background: status.bg, borderRadius: 999, padding: '3px 10px' }}>
            {status.label}
          </span>
          {canEdit && !done && due && !c.is_deleted && !c.archived && (
            <button type="button" className="btn btn-primary btn-sm" onClick={onMarkDone}>Mark as done</button>
          )}
        </div>
      </div>
      {row('Date & time', c.second_round_slot || '—')}
      {row('Mode', online ? 'Online' : 'In person')}
      {online
        ? row('Meeting link', c.second_round_meeting_link
          ? <a href={c.second_round_meeting_link} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{c.second_round_meeting_link}</a>
          : '—')
        : row('Address', c.second_round_location || '—')}
      {done && row('Marked done', formatLocalDateTime(c.second_round_completed_at))}
      {done && row('Feedback', c.second_round_feedback
        ? <span style={{ whiteSpace: 'pre-wrap' }}>{c.second_round_feedback}</span>
        : <span style={{ color: 'var(--text-muted)' }}>No feedback added</span>)}
    </div>
  );
}

// Naive UTC from the API ("2026-09-14T11:24:04") shown in the viewer's time.
function formatLocalDateTime(value) {
  if (!value) return '—';
  const iso = /Z$|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function UploadedResumeCard({
  text, filename, hasFile, onView, onRescan, onAttach, attaching,
  canRescan, canAttach, rescanDisabled, attachDisabled, rescanTitle, attachTitle, rescanning,
}) {
  const body = (text || '').trim();
  const preview = body.slice(0, 720);
  const fileRef = React.useRef(null);
  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div>
          <div className="section-title" style={{ marginBottom: 4 }}>Uploaded resume</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {hasFile
              ? `Stored against this candidate${filename ? ` · ${filename}` : ''}`
              : 'No original file stored yet. Attach a PDF or DOCX against this candidate.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {(hasFile || !!body) && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onView}>
              <FileText size={14} strokeWidth={2} />
              {hasFile ? 'View resume' : 'View parsed text'}
            </button>
          )}
          {canAttach && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) onAttach(file);
                }}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => fileRef.current?.click()}
                disabled={attachDisabled || attaching}
                title={attachTitle}
              >
                {attaching ? 'Scanning…' : (hasFile ? 'Replace file' : 'Attach file')}
              </button>
            </>
          )}
          {canRescan && !!body && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={onRescan} disabled={rescanDisabled || rescanning} title={rescanTitle}>
              {rescanning
                ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Scanning…</>
                : <><RefreshCw size={14} strokeWidth={2} /> Rescan</>}
            </button>
          )}
        </div>
      </div>
      {body ? (
        <pre style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit',
          maxHeight: 220, overflow: 'hidden', lineHeight: 1.55, margin: 0,
        }}>
          {preview}{body.length > preview.length ? '…' : ''}
        </pre>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {hasFile ? 'Original file is stored. Open it to view or download.' : 'No resume is stored for this candidate.'}
        </div>
      )}
    </div>
  );
}

function ResumeViewerModal({ candidateId, name, filename, hasFile, text, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [fileType, setFileType] = useState('');
  const [loading, setLoading] = useState(!!hasFile);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!hasFile || !candidateId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const holder = { url: null };
    candidatesApi.fetchResume(candidateId, true)
      .then(({ blob, type }) => {
        if (cancelled) return;
        holder.url = URL.createObjectURL(blob);
        setBlobUrl(holder.url);
        setFileType(type || blob.type || '');
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Could not open the stored resume.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (holder.url) URL.revokeObjectURL(holder.url);
    };
  }, [candidateId, hasFile]);

  const isPdf = /pdf/i.test(fileType) || /\.pdf$/i.test(filename || '');

  const download = async () => {
    if (hasFile) {
      const { blob } = await candidatesApi.fetchResume(candidateId, false);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${(name || 'resume').replace(/[^\w.-]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return;
    }
    const blob = new Blob([text || ''], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(name || 'resume').replace(/[^\w.-]+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="resume-viewer-title" style={{ maxWidth: isPdf && blobUrl ? 860 : 720, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div>
            <h3 id="resume-viewer-title" style={{ margin: 0 }}>Uploaded resume</h3>
            <p className="modal-sub" style={{ margin: '6px 0 0' }}>
              {hasFile
                ? (filename || 'Original file stored against this candidate.')
                : `Parsed text for ${name || 'this candidate'}. Original file was not stored.`}
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close" style={{ padding: 6 }}>
            <X size={16} />
          </button>
        </div>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <span className="spinner" style={{ width: 22, height: 22 }} />
          </div>
        ) : error ? (
          <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 12 }}>{error}</div>
        ) : isPdf && blobUrl ? (
          <iframe
            title="Uploaded resume"
            src={blobUrl}
            style={{ width: '100%', height: '65vh', border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}
          />
        ) : (
          <pre style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8,
            padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit',
            maxHeight: '60vh', overflowY: 'auto', lineHeight: 1.55, margin: 0,
          }}>
            {text?.trim() || (hasFile ? 'Original file is stored. Use Download to open it.' : 'No resume text was stored for this candidate.')}
          </pre>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
          {(hasFile || !!text?.trim()) && (
            <button type="button" className="btn btn-secondary" onClick={download}>
              <Download size={14} strokeWidth={2} /> Download
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Archive Modal ──────────────────────────────────────────────────────────
function ArchiveModal({ onClose, onConfirm, interviewScheduled }) {
  const [reason, setReason] = useState('');
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 24,
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 16, padding: 28,
        width: '100%', maxWidth: 440, border: '1px solid var(--border)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Archive Candidate</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Archived candidates are hidden from default views but can be restored later.
          {interviewScheduled
            ? ' A scheduled interview will be canceled and a cancellation email will be sent to the candidate.'
            : ''}
        </div>
        <textarea
          placeholder="Reason for archiving (optional)"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          style={{
            width: '100%', padding: '10px 12px', boxSizing: 'border-box',
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text-primary)', fontSize: 13,
            resize: 'vertical', marginBottom: 16,
          }}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => onConfirm(reason)}
            style={{ flex: 2, background: 'var(--red)', borderColor: 'var(--red)' }}
          >Archive</button>
        </div>
      </div>
    </div>
  );
}

function InterviewRecordingPlayer({ candidateId, video }) {
  if (!video) return null;
  const src = interviewsApi.videoUrl(candidateId, video.filename);
  const mb = video.size_bytes ? (video.size_bytes / (1024 * 1024)).toFixed(1) : null;
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Video size={15} strokeWidth={2} /> {video.question_id || 'Interview recording'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            Saved automatically after the interview{mb ? ` · ${mb} MB` : ''}
          </div>
        </div>
        <a href={src} download={video.filename} className="btn btn-ghost btn-sm">Download</a>
      </div>
      <div style={{ background: '#080808', aspectRatio: '16/9' }}>
        <video key={video.filename} controls playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'contain' }} src={src} />
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [job, setJob] = useState(null);
  // Only the person the candidate's job is assigned to acts on the candidate.
  const mayRecruit = canWorkJob(user, job);
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);
  const [attachingResume, setAttachingResume] = useState(false);
  const [tab, setTab] = useState('profile');
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showSecondRound, setShowSecondRound] = useState(false);
  const [outcomeMode, setOutcomeMode] = useState(null); // 'done' | 'edit' | null
  // Ticks so "Mark as done" appears when the round's time arrives, without
  // needing a reload of a page that was opened earlier.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const [showArchive, setShowArchive] = useState(false);
  const [deletingVideo, setDeletingVideo] = useState(null);

  // Manual score override state
  const [overrideScore, setOverrideScore] = useState('');
  const [overrideNotes, setOverrideNotes] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [showScoreCalc, setShowScoreCalc] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Archive state
  const [archiving, setArchiving] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);

  // Soft delete state
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Reminder state
  const [sendingReminder, setSendingReminder] = useState(null);
  const [cancelingInterview, setCancelingInterview] = useState(false);
  const [showCancelInterview, setShowCancelInterview] = useState(false);
  const [showCancelSecondRound, setShowCancelSecondRound] = useState(false);
  const [cancelingSecondRound, setCancelingSecondRound] = useState(false);
  const [showNextRoundConfirm, setShowNextRoundConfirm] = useState(false);
  const [nextRoundBusy, setNextRoundBusy] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdBusy, setHoldBusy] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectBusy, setRejectBusy] = useState(false);

  // Inline video player in transcript (SOW 6.7)
  const [inlineVideo, setInlineVideo] = useState(null); // filename currently expanded
  const [proctoring, setProctoring] = useState(null);
  const [proctoringLoading, setProctoringLoading] = useState(false);

  useEffect(() => {
    candidatesApi.get(id)
      .then(async (cand) => {
        setCandidate(cand);
        if (cand.job_id) {
          try { const j = await jobsApi.get(cand.job_id); setJob(j); } catch {}
        }
      })
      .catch(() => navigate('/candidates'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  useEffect(() => {
    interviewsApi.getVideos(id)
      .then(data => {
        const list = data.videos || [];
        setVideos(list);
        if (list.length && !activeVideo) setActiveVideo(list[0].filename);
      })
      .catch(() => setVideos([]));
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (tab === 'recordings' || tab === 'interview' || tab === 'proctoring') {
      setVideosLoading(true);
      interviewsApi.getVideos(id)
        .then(data => {
          const list = data.videos || [];
          setVideos(list);
          if (tab === 'recordings' && list.length) setActiveVideo(list[0].filename);
        })
        .catch(() => setVideos([]))
        .finally(() => setVideosLoading(false));
    }
    if (tab === 'proctoring') {
      setProctoringLoading(true);
      interviewsApi.getProctoring(id)
        .then(setProctoring)
        .catch(() => setProctoring(null))
        .finally(() => setProctoringLoading(false));
    }
  }, [tab, id]); // eslint-disable-line

  const handleRescanResume = async () => {
    setRescanning(true);
    try {
      const result = await candidatesApi.rescanResume(id);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
      const fallback = result?.scored_by === 'fallback'
        || /unavailable|exhausted|rule-based/i.test(result?.summary || '');
      showAlert({
        type: fallback ? 'warning' : 'success',
        title: fallback ? 'Resume rescanned without AI score' : 'Resume rescanned',
        message: fallback
          ? (result.summary || 'Profile was refreshed. Score was saved with the rule-based fallback.')
          : `Profile refreshed. Resume score: ${result.resume_score ?? result.total_score}/100${result.score_band ? ` (${result.score_band})` : ''}.`,
      });
    } catch (e) {
      showAlert({
        type: 'error',
        title: 'Could not rescan resume',
        message: e.message || 'Something went wrong. Please try again.',
      });
    } finally { setRescanning(false); }
  };

  const handleAttachResume = async (file) => {
    if (!file) return;
    setAttachingResume(true);
    try {
      const updated = await candidatesApi.uploadResumeFile(id, file);
      setCandidate(updated);
      const fallback = updated?.scored_by === 'fallback' || updated?.resume_scored_by === 'fallback'
        || /unavailable|exhausted|rule-based/i.test(updated?.summary || updated?.resume_score_summary || '');
      const score = updated?.resume_score;
      showAlert({
        type: fallback ? 'warning' : 'success',
        title: fallback ? 'Resume replaced; scored without AI' : 'Resume replaced and rescanned',
        message: fallback
          ? (updated.summary || 'File was saved and the profile was refreshed. Score was saved with the rule-based fallback.')
          : `File saved. Profile refreshed. Resume score: ${score ?? '—'}/100${updated.score_band || updated.resume_score_band ? ` (${updated.score_band || updated.resume_score_band})` : ''}.`,
      });
    } catch (e) {
      showAlert({
        type: 'error',
        title: 'Could not replace resume',
        message: e.message || 'Something went wrong. Please try again.',
      });
    } finally { setAttachingResume(false); }
  };

  const handleSaveProfile = async (payload) => {
    setSavingProfile(true);
    try {
      const updated = await candidatesApi.updateProfile(id, payload);
      setCandidate(updated);
      setShowEditProfile(false);
      showAlert({ type: 'success', title: 'Profile updated', message: 'Candidate details were saved.' });
    } catch (e) {
      showAlert({
        type: 'error',
        title: 'Could not update profile',
        message: e.message || 'Something went wrong. Please try again.',
      });
    } finally { setSavingProfile(false); }
  };

  const handleDecision = async (decision, notes) => {
    try {
      await candidatesApi.updateDecision(id, decision, notes);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
      return true;
    } catch (e) {
      const labels = { Accepted: 'selected', Rejected: 'rejected', Hold: 'on hold', 'Next Round': 'next round' };
      showAlert({ type: 'error', title: `Could not mark ${labels[decision] || decision.toLowerCase()}`, message: e.message || 'Something went wrong. Please try again.' });
      return false;
    }
  };

  const handleConfirmHold = async (reason) => {
    setHoldBusy(true);
    try {
      const ok = await handleDecision('Hold', reason);
      if (ok) setShowHoldModal(false);
    } finally {
      setHoldBusy(false);
    }
  };

  const handleConfirmReject = async (reason) => {
    setRejectBusy(true);
    try {
      const ok = await handleDecision('Rejected', reason);
      if (ok) setShowRejectModal(false);
    } finally {
      setRejectBusy(false);
    }
  };

  const handleConfirmNextRound = async () => {
    setNextRoundBusy(true);
    try {
      const ok = await handleDecision('Next Round');
      if (ok) setShowNextRoundConfirm(false);
    } finally {
      setNextRoundBusy(false);
    }
  };

  const handleOutcomeSaved = async () => {
    const mode = outcomeMode;
    setOutcomeMode(null);
    setCandidate(await candidatesApi.get(id));
    showAlert({
      type: 'success',
      title: mode === 'done' ? '2nd round marked done' : '2nd round updated',
      message: mode === 'done' ? 'You can now select, reject, or put the candidate on hold.' : 'Your changes are saved.',
    });
  };

  const handleDeleteVideo = async (filename) => {
    if (!window.confirm(`Delete recording "${filename}"? This cannot be undone.`)) return;
    setDeletingVideo(filename);
    try {
      await interviewsApi.deleteVideo(id, filename);
      const updated = videos.filter(v => v.filename !== filename);
      setVideos(updated);
      if (activeVideo === filename) setActiveVideo(updated[0]?.filename || null);
    } catch (e) {
      showAlert({ type: 'error', title: 'Delete failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setDeletingVideo(null);
    }
  };

  const handleOverrideScore = async () => {
    const score = parseInt(overrideScore);
    if (isNaN(score) || score < 0 || score > 100) {
      return showAlert({ type: 'warning', title: 'Invalid score', message: 'Score must be between 0 and 100.' });
    }
    setSavingOverride(true);
    try {
      await candidatesApi.overrideScore(id, score, overrideNotes);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
      setShowOverrideForm(false);
      setOverrideScore('');
      setOverrideNotes('');
      showAlert({ type: 'success', title: 'Score updated', message: 'Score override saved.' });
    } catch (e) {
      showAlert({ type: 'error', title: 'Override failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setSavingOverride(false);
    }
  };

  const handleArchive = async (reason) => {
    setArchiving(true);
    setShowArchive(false);
    try {
      const result = await candidatesApi.archive(id, reason);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
      if (result.interview_canceled || result.second_round_canceled) {
        showAlert({
          type: 'success',
          title: 'Archived',
          message: emailDeliveryNote(result) || (result.email_sent
            ? 'The scheduled interview was canceled and the candidate was emailed.'
            : 'The scheduled interview was canceled.'),
        });
      }
    } catch (e) {
      showAlert({ type: 'error', title: 'Archive failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    setUnarchiving(true);
    try {
      await candidatesApi.unarchive(id);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
    } catch (e) {
      showAlert({ type: 'error', title: 'Unarchive failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setUnarchiving(false);
    }
  };

  const handleSoftDelete = async () => {
    if (!window.confirm(
      c?.interview_status === 'Scheduled' || c?.interview_status === 'In Progress' || c?.pipeline_stage === 'second_round_scheduled'
        ? `Soft-delete ${c?.name || 'this candidate'}? The scheduled interview will be canceled and a cancellation email will be sent. They will be hidden from all views but can be restored later.`
        : `Soft-delete ${c?.name || 'this candidate'}? They will be hidden from all views but can be restored later.`
    )) return;
    setDeleting(true);
    try {
      const result = await candidatesApi.softDelete(id);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
      if (result.interview_canceled || result.second_round_canceled) {
        showAlert({
          type: 'success',
          title: 'Deleted',
          message: emailDeliveryNote(result) || (result.email_sent
            ? 'The scheduled interview was canceled and the candidate was emailed.'
            : 'The scheduled interview was canceled.'),
        });
      }
    } catch (e) {
      showAlert({ type: 'error', title: 'Delete failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await candidatesApi.restore(id);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
    } catch (e) {
      showAlert({ type: 'error', title: 'Restore failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setRestoring(false);
    }
  };

  const handleCancelInterview = async () => {
    setCancelingInterview(true);
    try {
      const result = await interviewsApi.cancelInterview(id);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
      setShowCancelInterview(false);
      showAlert({
        type: 'success',
        title: 'Interview canceled',
        message: emailDeliveryNote(result),
      });
    } catch (e) {
      showAlert({ type: 'error', title: 'Could not cancel interview', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setCancelingInterview(false);
    }
  };

  const handleCancelSecondRound = async () => {
    setCancelingSecondRound(true);
    try {
      const result = await interviewsApi.cancelSecondRound(id);
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
      setShowCancelSecondRound(false);
      showAlert({
        type: 'success',
        title: 'Interview canceled',
        message: emailDeliveryNote(result),
      });
    } catch (e) {
      showAlert({ type: 'error', title: 'Could not cancel interview', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setCancelingSecondRound(false);
    }
  };

  const handleSendReminder = async (type) => {
    setSendingReminder(type);
    try {
      const result = await interviewsApi.sendReminder(id, type);
      showAlert({ type: 'success', title: 'Reminder sent', message: result.message || `Reminder (${type}) sent.` });
      const updated = await candidatesApi.get(id);
      setCandidate(updated);
    } catch (e) {
      showAlert({ type: 'error', title: 'Reminder failed', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setSendingReminder(null);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
      <div className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );

  const c = candidate;
  const threshold = job?.resume_score_threshold ?? 75;
  const jobActive = !job || job.status === 'Active';
  const jobLockHint = job && job.status !== 'Active'
    ? `This job is ${job.status}. Set it to Active to take actions.`
    : undefined;
  const passedThreshold = (c.resume_score ?? 0) >= threshold;
  const interviewLocked = c.interview_status === 'Scheduled' || c.interview_status === 'In Progress';
  const inactive = c.is_deleted || c.archived;
  const canScheduleAi = mayRecruit && !inactive && !interviewLocked && c.interview_status !== 'Completed';
  // Scheduling ends once the 2nd round is held or the candidate is decided.
  const secondRoundClosed = ['second_round_completed', 'completed', ...REJECTED_STAGES].includes(c.pipeline_stage);
  const nextRoundSelected = c.recruiter_decision === 'Next Round'
    || c.pipeline_stage === 'second_round_invited'
    || ['second_round_scheduled', 'second_round_completed', 'completed'].includes(c.pipeline_stage)
    || (!!c.second_round_invited && !REJECTED_STAGES.includes(c.pipeline_stage) && c.recruiter_decision !== 'Hold' && c.recruiter_decision !== 'Rejected');
  const rejected = c.recruiter_decision === 'Rejected' || REJECTED_STAGES.includes(c.pipeline_stage);
  const showInterviewActions = mayRecruit && c.pipeline_stage === 'interview_completed'
    && c.recruiter_decision !== 'Next Round'
    && !rejected
    && !inactive;
  const showFinalActions = mayRecruit && ['second_round_completed', 'completed'].includes(c.pipeline_stage)
    && !rejected
    && !inactive;
  const secondRoundScheduled = c.pipeline_stage === 'second_round_scheduled';
  // The round is treated as under way for this long after its start time —
  // cancelling or rebooking a meeting that is happening right now helps nobody.
  // Matches SECOND_ROUND_OVERDUE_MINUTES on the server.
  const SECOND_ROUND_RUNNING_MINUTES = 35;
  // No time at all only if the slot text could not be read; then allow it.
  const secondRoundDue = secondRoundScheduled
    && (!c.second_round_at
      || new Date(c.second_round_at).getTime() + SECOND_ROUND_RUNNING_MINUTES * 60 * 1000 <= now);
  // Once its time has passed the recruiter either marks it done or books
  // another time — the app cannot tell whether an in-person or external
  // meeting happened. Cancelling is only for a round still ahead.
  const canScheduleSecondRound = mayRecruit && !inactive && c.interview_status === 'Completed' && !secondRoundClosed && nextRoundSelected
    && c.recruiter_decision !== 'Hold' && c.pipeline_stage !== 'rejected_interview'
    && (!secondRoundScheduled || secondRoundDue);
  const interviewNextSelected = c.recruiter_decision === 'Next Round' || c.pipeline_stage === 'second_round_invited';
  const finalShortlisted = c.recruiter_decision === 'Accepted' || c.pipeline_stage === 'completed';
  const sourceColor = SOURCE_COLORS[c.source] || '#475569';

  const effectiveScore = c.manual_score ?? c.resume_score;
  const comm = communicationFromBreakdown(c.interview_score_breakdown);

  return (
    <div>
      {showResume && (
        <ResumeViewerModal
          candidateId={c.id}
          name={c.name}
          filename={c.resume_filename}
          hasFile={!!c.has_resume_file}
          text={c.resume_text}
          onClose={() => setShowResume(false)}
        />
      )}
      {showEditProfile && (
        <EditProfileModal
          candidate={c}
          currency={job?.budget_currency}
          busy={savingProfile}
          onClose={() => { if (!savingProfile) setShowEditProfile(false); }}
          onSave={handleSaveProfile}
        />
      )}
      {showScoreCalc && (
        <ScoreCalculationModal
          calculation={c.score_calculation}
          savedScore={c.resume_score}
          onClose={() => setShowScoreCalc(false)}
        />
      )}
      {showSchedule && (
        <ScheduleModal
          candidate={c}
          jobTitle={job?.title || 'Position'}
          onClose={() => setShowSchedule(false)}
          onScheduled={(data) => {
            setCandidate(prev => ({
              ...prev,
              interview_status: 'Scheduled',
              interview_slot: data.slot,
              teams_meeting_link: data.meeting_link || data.teams_meeting_link,
              teams_meeting_id: data.room_code || data.teams_meeting_id,
            }));
          }}
        />
      )}

      {showSecondRound && (
        <SecondRoundModal
          candidate={c}
          jobTitle={job?.title || 'Position'}
          defaults={{
            platform: job?.second_round_platform,
            meeting_link: job?.second_round_meeting_link,
            location: job?.second_round_location,
          }}
          onClose={() => setShowSecondRound(false)}
          onScheduled={(data) => {
            setCandidate(prev => ({
              ...prev,
              pipeline_stage: data.pipeline_stage,
              second_round_slot: data.slot,
              second_round_type: data.meeting_type,
              second_round_meeting_link: data.meeting_link,
              second_round_location: data.location,
            }));
            // Reload for second_round_at, which decides when Mark Done appears.
            candidatesApi.get(id).then(setCandidate).catch(() => {});
          }}
        />
      )}

      {outcomeMode && (
        <SecondRoundOutcomeModal
          candidate={c}
          mode={outcomeMode}
          onClose={() => setOutcomeMode(null)}
          onSaved={handleOutcomeSaved}
        />
      )}

      {showNextRoundConfirm && (
        <NextRoundConfirmModal
          name={c.name}
          busy={nextRoundBusy}
          onClose={() => { if (!nextRoundBusy) setShowNextRoundConfirm(false); }}
          onConfirm={handleConfirmNextRound}
        />
      )}

      {showHoldModal && (
        <HoldReasonModal
          name={c.name}
          busy={holdBusy}
          onClose={() => { if (!holdBusy) setShowHoldModal(false); }}
          onConfirm={handleConfirmHold}
        />
      )}

      {showRejectModal && (
        <RejectReasonModal
          name={c.name}
          busy={rejectBusy}
          onClose={() => { if (!rejectBusy) setShowRejectModal(false); }}
          onConfirm={handleConfirmReject}
        />
      )}

      {showArchive && (
        <ArchiveModal
          interviewScheduled={c.interview_status === 'Scheduled' || c.interview_status === 'In Progress' || c.pipeline_stage === 'second_round_scheduled'}
          onClose={() => setShowArchive(false)}
          onConfirm={handleArchive}
        />
      )}

      {showCancelInterview && (
        <CancelInterviewModal
          busy={cancelingInterview}
          onClose={() => { if (!cancelingInterview) setShowCancelInterview(false); }}
          onConfirm={handleCancelInterview}
        />
      )}

      {showCancelSecondRound && (
        <CancelInterviewModal
          busy={cancelingSecondRound}
          title="Cancel this interview?"
          message="The scheduled second round will be canceled, and a cancellation email will be sent to the candidate. You can schedule a new second-round meeting afterwards."
          confirmLabel="Cancel interview"
          keepLabel="Keep interview"
          onClose={() => { if (!cancelingSecondRound) setShowCancelSecondRound(false); }}
          onConfirm={handleCancelSecondRound}
        />
      )}

      <div className="page-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            <Link to="/candidates" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Candidates</Link> / {c.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div className="page-title" style={{ margin: 0 }}>{c.name || 'Unknown Candidate'}</div>
            {mayRecruit && !inactive && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowEditProfile(true)}
                disabled={!jobActive}
                title={jobLockHint}
              >
                <Pencil size={14} strokeWidth={2} />
                Edit profile
              </button>
            )}
          </div>
          {(c.email || c.phone || c.location || c.work_mode) && (
            <div style={{
              marginTop: 8, fontSize: 13, color: 'var(--text-secondary)',
              display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'center',
            }}>
              {c.email && (
                <a href={`mailto:${c.email}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
                  {c.email}
                </a>
              )}
              {c.phone && <span>{c.phone}</span>}
              {c.location && <span>{c.location}</span>}
              {c.work_mode && <span>{c.work_mode}</span>}
            </div>
          )}
          {(c.current_ctc || c.expected_ctc || c.notice_period) && (
            <div style={{
              marginTop: 4, fontSize: 12, color: 'var(--text-muted)',
              display: 'flex', flexWrap: 'wrap', gap: '4px 16px',
            }}>
              {c.current_ctc && <span>Current CTC {c.current_ctc}</span>}
              {c.expected_ctc && <span>Expected CTC {c.expected_ctc}</span>}
              {c.notice_period && <span>Notice {c.notice_period}</span>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.current_role} {c.current_company ? `@ ${c.current_company}` : ''}</div>
            {c.source && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                background: `${sourceColor}20`, color: sourceColor, border: `1px solid ${sourceColor}40`
              }}>{c.source}</span>
            )}
            {c.archived && (
              <span className="badge badge-gray" style={{ fontSize: 11 }}>Archived</span>
            )}
            {c.is_deleted && (
              <span className="badge badge-red" style={{ fontSize: 11 }}>Deleted</span>
            )}
          </div>
          {job && (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
              <Link to={`/jobs/${job.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 700 }}>
                {job.title}
              </Link>
              {job.department ? ` · ${job.department}` : ''}
              {job.status && job.status !== 'Active' ? ` · ${job.status}` : ''}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canScheduleAi && (
            <button className="btn btn-primary" onClick={() => setShowSchedule(true)} disabled={!jobActive} title={jobLockHint}>
              <CalendarDays size={14} strokeWidth={2} />
              Schedule AI Interview
            </button>
          )}
          {mayRecruit && !inactive && secondRoundDue && (
            <button className="btn btn-primary" onClick={() => setOutcomeMode('done')} disabled={!jobActive} title={jobLockHint}>
              <ClipboardCheck size={14} strokeWidth={2} />
              Mark 2nd Round Done
            </button>
          )}
          {canScheduleSecondRound && (
            <button
              className="btn btn-primary"
              onClick={() => setShowSecondRound(true)}
              disabled={!jobActive}
              title={jobLockHint}
            >
              <CalendarClock size={14} strokeWidth={2} />
              {secondRoundScheduled ? 'Reschedule 2nd Round' : 'Schedule 2nd Round'}
            </button>
          )}
          {mayRecruit && secondRoundScheduled && !secondRoundDue && !inactive && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
              onClick={() => setShowCancelSecondRound(true)}
              disabled={!jobActive || cancelingSecondRound}
              title={jobLockHint}
            >
              {cancelingSecondRound ? 'Canceling…' : 'Cancel interview'}
            </button>
          )}
          {c.interview_missed_at && c.interview_status === 'Canceled' && (
            <span style={{
              fontSize: 12, background: 'rgba(234,88,12,0.1)', color: '#ea580c',
              borderRadius: 8, padding: '6px 12px', fontWeight: 600,
              border: '1px solid rgba(234,88,12,0.3)',
            }}>
              <CalendarClock size={12} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} />{' '}
              Not attended — interview canceled automatically
            </span>
          )}
          {mayRecruit && interviewLocked && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
              onClick={() => setShowCancelInterview(true)}
              disabled={!jobActive || cancelingInterview}
              title={jobLockHint}
            >
              {cancelingInterview ? 'Canceling…' : 'Cancel Interview'}
            </button>
          )}
          {/* Only while the round is still ahead. Once its time has passed the
              two buttons say what to do — mark it done, or book another time —
              and the date stays on the 2nd Round card below. */}
          {secondRoundScheduled && !secondRoundDue && c.second_round_slot && (
            <span style={{
              fontSize: 12, background: 'rgba(234,88,12,0.1)', color: '#ea580c',
              borderRadius: 8, padding: '6px 12px', fontWeight: 600,
              border: '1px solid rgba(234,88,12,0.3)',
            }}>
              <CalendarClock size={12} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} />{' '}
              Scheduled: {c.second_round_slot}
            </span>
          )}
          {c.interview_status === 'Scheduled' && (
            <span style={{ fontSize: 12, background: 'rgba(37,99,235,0.1)', color: 'var(--accent)', borderRadius: 8, padding: '6px 12px', fontWeight: 600 }}>
              Scheduled: {c.interview_slot}
            </span>
          )}
          {/* Hidden for now — recruiter text / video interview
          {c.resume_score !== null && c.interview_status !== 'Completed' && (
            <>
              <Link to={`/interview/${c.id}`} className="btn btn-secondary">Text Interview</Link>
              <Link to={`/video-interview/${c.id}`} className="btn btn-primary">Video Interview</Link>
            </>
          )}
          */}
          {/* Archive / Unarchive */}
          {mayRecruit && !c.is_deleted && (
            c.archived ? (
              <button className="btn btn-ghost btn-sm" onClick={handleUnarchive} disabled={!jobActive || unarchiving}
                title={jobLockHint}
                style={{ color: 'var(--green)', borderColor: 'var(--green)' }}>
                {unarchiving ? 'Restoring...' : 'Unarchive'}
              </button>
            ) : (
              <button className="btn btn-archive btn-sm" onClick={() => setShowArchive(true)} disabled={!jobActive || archiving} title={jobLockHint}>
                Archive
              </button>
            )
          )}
          {/* Soft Delete / Restore */}
          {!mayRecruit ? null : c.is_deleted ? (
            <button className="btn btn-ghost btn-sm" onClick={handleRestore} disabled={!jobActive || restoring}
              title={jobLockHint}
              style={{ color: 'var(--green)', borderColor: 'var(--green)' }}>
              {restoring ? 'Restoring...' : 'Restore'}
            </button>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={handleSoftDelete} disabled={!jobActive || deleting}
              title={jobLockHint}
              style={{ color: 'var(--red)', borderColor: 'var(--red)' }}>
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {/* Job details */}
      {job && (
        <div className="card" style={{ marginBottom: 16, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
            Job
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <Link to={`/jobs/${job.id}`} style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-heading)', textDecoration: 'none' }}>
                {job.title}
              </Link>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {[job.department, job.employment_type, job.work_location].filter(Boolean).join(' · ') || 'Open role'}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginTop: 12, marginBottom: 6 }}>
            Required skills
          </div>
          <div className="tags-wrap">
            {(job.required_skills || []).length
              ? (job.required_skills || []).map((s) => (
                <span key={s} className="tag" style={{ color: 'var(--accent)' }}>{s}</span>
              ))
              : <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No required skills listed</span>}
          </div>
        </div>
      )}

      {job && job.status !== 'Active' && (
        <div style={{
          background: 'rgba(210,153,34,0.08)', border: '1px solid rgba(210,153,34,0.35)',
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: 'var(--yellow)',
        }}>
          This job is <strong>{job.status}</strong>. Set it to Active to upload resumes or take actions on candidates.
        </div>
      )}

      {/* Pipeline Progress */}
      {c.pipeline_stage && <PipelineProgress stage={c.pipeline_stage} emailSent={c.rejection_email_sent} reason={c.recruiter_notes} />}

      {c.recruiter_decision === 'Hold' && c.pipeline_stage === 'interview_completed' && (
        <div style={{
          background: 'rgba(210,153,34,0.08)', border: '1px solid rgba(210,153,34,0.35)',
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: 'var(--yellow)',
        }}>
          <strong>On hold</strong> — a second-round meeting cannot be created until they are selected for the next round.
          {c.recruiter_notes ? ` Reason: ${c.recruiter_notes}` : ''}
        </div>
      )}

      {c.recruiter_decision === 'Hold' && ['second_round_completed', 'completed'].includes(c.pipeline_stage) && (
        <div style={{
          background: 'rgba(210,153,34,0.08)', border: '1px solid rgba(210,153,34,0.35)',
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: 'var(--yellow)',
        }}>
          <strong>On hold</strong> — Select and Reject are still available when you are ready to decide.
          {c.recruiter_notes ? ` Reason: ${c.recruiter_notes}` : ''}
        </div>
      )}

      {/* Deleted banner */}
      {c.is_deleted && (
        <div style={{
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: 'var(--red)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>
            <strong>Deleted</strong>
            {c.deleted_at ? ` — ${new Date(c.deleted_at).toLocaleString()}` : ''}
            {' · This candidate is hidden from all list views.'}
          </span>
          {mayRecruit && <button className="btn btn-ghost btn-sm" onClick={handleRestore} style={{ color: 'var(--green)' }}>
            Restore
          </button>}
        </div>
      )}

      {/* Archive banner */}
      {c.archived && !c.is_deleted && (
        <div style={{
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: 'var(--red)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>
            <strong>Archived</strong>
            {c.archived_reason ? ` — ${c.archived_reason}` : ''}
          </span>
          {mayRecruit && <button className="btn btn-ghost btn-sm" onClick={handleUnarchive} style={{ color: 'var(--green)' }}>
            Restore
          </button>}
        </div>
      )}

      {/* Score summary bar */}
      <div className="card" style={{ marginBottom: 20, display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { label: 'Resume Score', value: c.resume_score, max: 100 },
          { label: c.manual_score !== null && c.manual_score !== undefined ? 'Manual Override' : 'Interview Score', value: c.manual_score ?? c.interview_score, max: 100 },
          { label: 'Final Score', value: c.final_score, max: 100 },
        ].map(({ label, value }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-mono)', color: value ? SCORE_COLOR(value) : 'var(--text-muted)' }}>
              {value ?? '—'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
          </div>
        ))}

        {c.interview_status === 'Completed' && (
          <CommunicationBadge comm={comm} />
        )}

        {/* Manual override indicator */}
        {c.manual_score !== null && c.manual_score !== undefined && (
          <div style={{ fontSize: 12, background: 'rgba(245,158,11,0.08)', borderRadius: 8, padding: '6px 10px', color: 'var(--yellow)', border: '1px solid rgba(245,158,11,0.3)' }}>
            <div style={{ fontWeight: 600 }}>Manual Override Active</div>
            {c.manual_score_notes && <div style={{ fontSize: 11, opacity: 0.8 }}>{c.manual_score_notes}</div>}
          </div>
        )}

        {c.interview_status === 'Scheduled' && c.teams_meeting_link && (
          <div style={{ fontSize: 12, background: 'rgba(37,99,235,0.08)', borderRadius: 8, padding: '8px 12px', color: 'var(--accent)' }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Portal Meeting Room</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{c.teams_meeting_id}</div>
          </div>
        )}

        <div style={{ flex: 1 }} />
        {showInterviewActions && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className={`btn btn-sm ${interviewNextSelected ? 'btn-primary' : 'btn-ghost'}`}
              disabled={!jobActive}
              title={jobLockHint}
              onClick={() => setShowNextRoundConfirm(true)}
            >
              Select for next round
            </button>
            <button
              className="btn btn-sm btn-ghost"
              disabled={!jobActive}
              title={jobLockHint}
              onClick={() => setShowRejectModal(true)}
            >
              Reject
            </button>
            {c.recruiter_decision !== 'Hold' && (
              <button
                className="btn btn-sm btn-ghost"
                disabled={!jobActive}
                title={jobLockHint}
                onClick={() => setShowHoldModal(true)}
              >
                On hold
              </button>
            )}
          </div>
        )}
        {showFinalActions && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className={`btn btn-sm ${finalShortlisted ? 'btn-primary' : 'btn-ghost'}`}
              disabled={!jobActive || finalShortlisted}
              title={finalShortlisted ? 'This candidate is selected' : jobLockHint}
              onClick={() => handleDecision('Accepted')}
            >
              {finalShortlisted ? 'Selected' : 'Select'}
            </button>
            {!finalShortlisted && (
              <>
                <button
                  className="btn btn-sm btn-ghost"
                  disabled={!jobActive}
                  title={jobLockHint}
                  onClick={() => setShowRejectModal(true)}
                >
                  Reject
                </button>
                {c.recruiter_decision !== 'Hold' && (
                  <button
                    className="btn btn-sm btn-ghost"
                    disabled={!jobActive}
                    title={jobLockHint}
                    onClick={() => setShowHoldModal(true)}
                  >
                    On hold
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {c.second_round_slot && ['second_round_scheduled', 'second_round_completed', 'completed', 'rejected_final'].includes(c.pipeline_stage) && (
        <SecondRoundCard
          c={c}
          due={secondRoundDue}
          onMarkDone={() => setOutcomeMode('done')}
          canEdit={mayRecruit}
        />
      )}

      <div className="tabs">
        {[
          ['profile', 'Profile'],
          ['resume-score', 'Resume Score'],
          ['interview', 'Interview'],
          ['proctoring', 'Proctoring'],
          ['recordings', `Recordings${videos.length ? ` (${videos.length})` : ''}`],
        ].map(([k, l]) => (
          <button key={k} className={`tab-btn ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {/* ─── PROFILE ─── */}
      {tab === 'profile' && (
        <div className="grid-2">
          {videos.some(v => v.source === 'session' || String(v.filename || '').startsWith('session_')) && (
            <div style={{ gridColumn: '1 / -1' }}>
              <InterviewRecordingPlayer
                candidateId={c.id}
                video={videos.find(v => v.source === 'session' || String(v.filename || '').startsWith('session_'))}
              />
            </div>
          )}
          <UploadedResumeCard
            text={c.resume_text}
            filename={c.resume_filename}
            hasFile={!!c.has_resume_file}
            onView={() => setShowResume(true)}
            onRescan={handleRescanResume}
            onAttach={handleAttachResume}
            attaching={attachingResume}
            rescanning={rescanning}
            canRescan={mayRecruit && !inactive}
            canAttach={mayRecruit && !inactive}
            rescanDisabled={!jobActive || rescanning || attachingResume}
            attachDisabled={!jobActive || rescanning || attachingResume}
            rescanTitle={jobLockHint}
            attachTitle={jobLockHint}
          />
          <RequirementCoverage coverage={c.requirement_coverage} />
          {c.requirement_coverage?.must_have ? (
            <SkillCoverage coverage={c.requirement_coverage} skills={c.skills} />
          ) : (
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="section-title">Skills</div>
              <div className="tags-wrap">
                {(c.skills || []).map(s => <span key={s} className="tag">{s}</span>)}
              </div>
            </div>
          )}
          <div className="card">
            <div className="section-title">Certifications</div>
            {(c.certifications || []).length === 0
              ? <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>None listed</span>
              : <div className="tags-wrap">{(c.certifications || []).map(s => <span key={s} className="tag" style={{ color: 'var(--green)' }}>{s}</span>)}</div>
            }
          </div>
          <EducationList
            education={c.education}
            items={c.requirement_coverage?.education?.items}
          />
          <ProjectsList projects={c.projects} />
          <ExperienceTimeline history={c.employment_history} />
          <StrengthsAndRisks
            strengths={c.resume_score_strengths}
            gaps={c.resume_score_gaps}
            scoredBy={c.resume_scored_by}
            scored={c.resume_score !== null && c.resume_score !== undefined}
          />
        </div>
      )}

      {/* ─── RESUME SCORE ─── */}
      {tab === 'resume-score' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <UploadedResumeCard
              text={c.resume_text}
              filename={c.resume_filename}
              hasFile={!!c.has_resume_file}
              onView={() => setShowResume(true)}
              onRescan={handleRescanResume}
              onAttach={handleAttachResume}
              attaching={attachingResume}
              rescanning={rescanning}
              canRescan={mayRecruit && !inactive}
              canAttach={mayRecruit && !inactive}
              rescanDisabled={!jobActive || rescanning || attachingResume}
              attachDisabled={!jobActive || rescanning || attachingResume}
              rescanTitle={jobLockHint}
              attachTitle={jobLockHint}
            />
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            {c.resume_score == null ? (
              <div className="empty-state">
                <div className="empty-state-icon"><FileText size={40} strokeWidth={1.5} /></div>
                <div className="empty-state-title">Resume not scored yet</div>
                {mayRecruit && !inactive && <button className="btn btn-primary mt-4" onClick={handleRescanResume} disabled={!jobActive || rescanning} title={jobLockHint}>
                  {rescanning ? 'Scanning...' : 'Rescan Resume'}
                </button>}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
                  <div>
                    <div className="section-title">Resume Score Breakdown</div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: SCORE_COLOR(effectiveScore ?? c.resume_score), fontFamily: 'var(--font-mono)' }}>
                      {effectiveScore ?? c.resume_score}/100
                      {c.manual_score !== null && c.manual_score !== undefined && (
                        <span style={{ fontSize: 14, color: 'var(--yellow)', marginLeft: 10, fontWeight: 600 }}>
                          (AI: {c.resume_score})
                        </span>
                      )}
                    </div>
                    <span className={`badge ${c.resume_score_band === 'Strong Match' ? 'badge-green' : c.resume_score_band === 'Good Match' ? 'badge-blue' : c.resume_score_band === 'Partial Match' ? 'badge-yellow' : 'badge-red'}`}>
                      {c.resume_score_band}
                    </span>
                    {passedThreshold
                      ? <span className="badge badge-green" style={{ marginLeft: 6 }}>Passes Threshold ({threshold})</span>
                      : <span className="badge badge-red" style={{ marginLeft: 6 }}>Below Threshold ({threshold})</span>
                    }
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {c.score_calculation && (
                      <button className="btn btn-ghost btn-sm" onClick={() => setShowScoreCalc(true)}>
                        <Calculator size={14} strokeWidth={2} />
                        Calculation
                      </button>
                    )}
                    {mayRecruit && !inactive && <button className="btn btn-secondary btn-sm" onClick={handleRescanResume} disabled={!jobActive || rescanning} title={jobLockHint}>
                      {rescanning ? 'Scanning…' : 'Rescan'}
                    </button>}
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20, lineHeight: 1.7 }}>{c.resume_score_summary}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {Object.entries(c.resume_score_breakdown || {}).filter(([, val]) => val && typeof val.score === 'number' && val.max).map(([key, val]) => (
                    <div key={key}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{breakdownLabel(key)}</span>
                        <span style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: SCORE_COLOR((val.score / val.max) * 100) }}>{val.score}/{val.max}</span>
                      </div>
                      <div className="score-bar-track"><div className="score-bar-fill" style={{ width: `${(val.score / val.max) * 100}%`, background: SCORE_COLOR((val.score / val.max) * 100) }} /></div>
                      {val.rationale && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{val.rationale}</div>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Manual Score Override (SOW 4.11) */}
          {c.resume_score !== null && (
            <div className="card">
              <div className="section-title">Manual Score Override</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
                Override the AI-assigned resume score. The original AI score is preserved for reference.
              </div>

              {c.manual_score !== null && c.manual_score !== undefined && (
                <div style={{
                  background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
                  borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14,
                  color: 'var(--text-secondary)'
                }}>
                  <strong style={{ color: 'var(--yellow)' }}>Active Override:</strong> {c.manual_score}/100
                  {c.manual_score_notes && <span style={{ marginLeft: 8 }}>— {c.manual_score_notes}</span>}
                </div>
              )}

              {!mayRecruit ? null : !showOverrideForm ? (
                <button className="btn btn-ghost btn-sm" disabled={!jobActive} title={jobLockHint} onClick={() => {
                  setShowOverrideForm(true);
                  setOverrideScore(c.manual_score ?? c.resume_score ?? '');
                  setOverrideNotes(c.manual_score_notes ?? '');
                }}>
                  {c.manual_score !== null && c.manual_score !== undefined ? 'Edit Override' : 'Set Override Score'}
                </button>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Override Score (0–100)</label>
                      <input
                        type="number"
                        min="0" max="100"
                        value={overrideScore}
                        onChange={e => setOverrideScore(e.target.value)}
                        style={{
                          width: '100%', padding: '9px 12px', boxSizing: 'border-box',
                          background: 'var(--bg-surface)', border: '1px solid var(--border)',
                          borderRadius: 8, color: 'var(--text-primary)', fontSize: 14,
                        }}
                      />
                    </div>
                    <div style={{ marginTop: 18 }}>
                      {overrideScore !== '' && (
                        <span style={{
                          fontSize: 22, fontWeight: 700,
                          color: SCORE_COLOR(parseInt(overrideScore)),
                          fontFamily: 'var(--font-mono)',
                        }}>{overrideScore}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notes (reason for override)</label>
                    <textarea
                      value={overrideNotes}
                      onChange={e => setOverrideNotes(e.target.value)}
                      placeholder="e.g. Strong portfolio work not captured in resume"
                      rows={2}
                      style={{
                        width: '100%', padding: '9px 12px', boxSizing: 'border-box',
                        background: 'var(--bg-surface)', border: '1px solid var(--border)',
                        borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, resize: 'vertical',
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowOverrideForm(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={handleOverrideScore} disabled={!jobActive || savingOverride} title={jobLockHint}>
                      {savingOverride ? 'Saving...' : 'Save Override'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── INTERVIEW ─── */}
      {tab === 'interview' && (
        <div className="card">
          {c.interview_status === 'Scheduled' && c.teams_meeting_link && (
            <div style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--accent)' }}>Interview Scheduled</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
                <strong>Slot:</strong> {c.interview_slot}<br />
                <strong>Room code:</strong> {c.teams_meeting_id}
              </div>
              <a href={c.teams_meeting_link} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">Open Meeting Room</a>
            </div>
          )}
          {c.interview_status !== 'Completed' && c.interview_status !== 'Scheduled' ? (
            <div className="empty-state">
              <div className="empty-state-icon"><Mic size={40} strokeWidth={1.5} /></div>
              <div className="empty-state-title">Interview not completed</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
                  {canScheduleAi && (
                    <button className="btn btn-primary" onClick={() => setShowSchedule(true)} disabled={!jobActive} title={jobLockHint}>
                      Schedule AI Interview
                    </button>
                  )}
                  {/* Hidden for now — recruiter text / video interview
                  {c.resume_score !== null && (
                    <>
                      <Link to={`/interview/${c.id}`} className="btn btn-secondary">Text Interview</Link>
                      <Link to={`/video-interview/${c.id}`} className="btn btn-primary">Video Interview</Link>
                    </>
                  )}
                  */}
                </div>
            </div>
          ) : c.interview_status === 'Completed' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div className="section-title">Interview Score: <span style={{ color: SCORE_COLOR(c.interview_score) }}>{c.interview_score}/100</span></div>
                  <span className={`badge ${c.interview_recommendation === 'Recommended' ? 'badge-green' : c.interview_recommendation?.includes('Reservations') ? 'badge-yellow' : 'badge-red'}`}>
                    {c.interview_recommendation}
                  </span>
                </div>
                <CommunicationBadge comm={comm} size="lg" />
              </div>
              {comm?.notes && (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
                  {comm.notes}
                </div>
              )}

              <InterviewRecordingPlayer
                candidateId={c.id}
                video={videos.find(v => v.source === 'session' || String(v.filename || '').startsWith('session_')) || videos[0]}
              />

              {/* ── Linked transcript (SOW 6.7) ── */}
              <LinkedTranscript
                transcript={c.interview_transcript || []}
                videos={videos}
                candidateId={c.id}
                inlineVideo={inlineVideo}
                setInlineVideo={setInlineVideo}
              />
            </>
          ) : null}
        </div>
      )}

      {/* ─── PROCTORING ─── */}
      {tab === 'proctoring' && (
        <div>
          {proctoringLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <div className="spinner" style={{ width: 28, height: 28 }} />
            </div>
          ) : (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ShieldAlert size={16} /> Proctoring report
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                  Evidence only — flags are not an automatic reject. Review clips before deciding.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['NO_FACE', 'MULTI_FACE', 'LOOKING_AWAY', 'TAB_HIDDEN', 'LIP_AUDIO_MISMATCH', 'NOT_ACTIVE_SPEAKER'].map((key) => (
                    <div key={key} style={{
                      padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
                      background: (proctoring?.counts?.[key] || 0) ? 'rgba(217,45,32,0.08)' : 'var(--bg-hover)',
                      minWidth: 120,
                    }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{key.replace(/_/g, ' ')}</div>
                      <div style={{ fontWeight: 700 }}>{proctoring?.counts?.[key] || 0}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{ marginBottom: 16 }}>
                <div className="section-title">Flag timeline</div>
                {!(proctoring?.flags || []).length ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No flags recorded for this interview.</div>
                ) : (proctoring.flags || []).map((f, i) => (
                  <div key={`${f.flag_type}-${f.at}-${i}`} style={{
                    display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center',
                    padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 13,
                  }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{f.flag_type}{f.count > 1 ? ` ×${f.count}` : ''}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{f.detail || f.at}</div>
                    </div>
                    {f.clip && (
                      <button className="btn btn-secondary btn-sm" onClick={() => { setTab('recordings'); setActiveVideo(f.clip); }}>
                        <Play size={12} /> Clip
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {(proctoring?.clips || []).length > 0 && (
                <div className="card">
                  <div className="section-title">Evidence clips</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                    {proctoring.clips.map((clip) => (
                      <div key={clip.filename}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{clip.question_id}</div>
                        <video
                          src={interviewsApi.videoUrl(c.id, clip.filename)}
                          controls
                          style={{ width: '100%', borderRadius: 8, background: '#000' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── RECORDINGS ─── */}
      {tab === 'recordings' && (
        <div>
          {videosLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <div className="spinner" style={{ width: 28, height: 28 }} />
            </div>
          ) : videos.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon"><Video size={40} strokeWidth={1.5} /></div>
                <div className="empty-state-title">No video recordings yet</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
                  Start a video interview to record candidate responses. Completed AI meeting interviews are stored here automatically.
                </div>
                {/* Hidden for now — recruiter video interview
                {c.resume_score !== null && c.interview_status !== 'Completed' && (
                  <Link to={`/video-interview/${c.id}`} className="btn btn-primary">Start Video Interview</Link>
                )}
                */}
              </div>
            </div>
          ) : (
            <div className="r-split-240" style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16 }}>
              {/* Left — recording list */}
              <div className="card" style={{ padding: 0, overflow: 'hidden', alignSelf: 'start' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Recordings</span>
                  <span style={{ fontSize: 11, background: 'var(--accent)', color: '#fff', borderRadius: 10, padding: '2px 8px' }}>{videos.length}</span>
                </div>
                {videos.map((v, i) => {
                  const qLabel = v.question_id || `Recording ${i + 1}`;
                  const isActive = activeVideo === v.filename;
                  return (
                    <div key={v.filename} style={{ borderBottom: '1px solid var(--border)', borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent' }}>
                      <button
                        onClick={() => setActiveVideo(v.filename)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          width: '100%', padding: '10px 14px', textAlign: 'left',
                          background: isActive ? 'rgba(88,166,255,0.08)' : 'transparent',
                          border: 'none', cursor: 'pointer', transition: 'all 0.15s'
                        }}
                      >
                        <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', color: isActive ? 'var(--accent)' : 'var(--text-muted)', opacity: isActive ? 1 : 0.6 }}><PlayCircle size={18} strokeWidth={1.9} /></span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? 'var(--accent)' : 'var(--text-primary)' }}>{qLabel}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{(v.size_bytes / 1024).toFixed(0)} KB</div>
                        </div>
                        {mayRecruit && <button
                          onClick={e => { e.stopPropagation(); handleDeleteVideo(v.filename); }}
                          disabled={deletingVideo === v.filename}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--red)', fontSize: 14, padding: '2px 4px',
                            opacity: deletingVideo === v.filename ? 0.4 : 0.6,
                            transition: 'opacity 0.15s',
                          }}
                          title="Delete recording"
                        >x</button>}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Right — video player */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {activeVideo ? (
                  <>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {videos.find(v => v.filename === activeVideo)?.question_id || 'Recording'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{activeVideo}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <a href={interviewsApi.videoUrl(c.id, activeVideo)} download={activeVideo} className="btn btn-ghost btn-sm">Download</a>
                        {mayRecruit && <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--red)' }}
                          onClick={() => handleDeleteVideo(activeVideo)}
                          disabled={deletingVideo === activeVideo}
                        >
                          Delete
                        </button>}
                      </div>
                    </div>
                    <div style={{ background: '#080808', aspectRatio: '16/9' }}>
                      <video key={activeVideo} controls playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'contain' }} src={interviewsApi.videoUrl(c.id, activeVideo)} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
                      <button className="btn btn-ghost btn-sm"
                        disabled={videos.findIndex(v => v.filename === activeVideo) === 0}
                        onClick={() => { const idx = videos.findIndex(v => v.filename === activeVideo); if (idx > 0) setActiveVideo(videos[idx - 1].filename); }}><ArrowLeft size={13} strokeWidth={2} /> Previous</button>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
                        {videos.findIndex(v => v.filename === activeVideo) + 1} / {videos.length}
                      </span>
                      <button className="btn btn-ghost btn-sm"
                        disabled={videos.findIndex(v => v.filename === activeVideo) === videos.length - 1}
                        onClick={() => { const idx = videos.findIndex(v => v.filename === activeVideo); if (idx < videos.length - 1) setActiveVideo(videos[idx + 1].filename); }}>Next <ArrowRight size={13} strokeWidth={2} /></button>
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <div style={{ display: 'flex', justifyContent: 'center', color: 'var(--text-muted)' }}><PlayCircle size={40} strokeWidth={1.5} /></div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Select a recording to play</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── EMAIL & REMINDERS ─── */}
      {tab === 'comms' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Invite Email Status */}
          <div className="card">
            <div className="section-title">Interview Invite Status</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 8,
                background: c.ai_interview_invite_sent ? 'rgba(5,150,105,0.08)' : 'var(--bg-hover)',
                border: `1px solid ${c.ai_interview_invite_sent ? 'var(--green)' : 'var(--border)'}`,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', color: c.ai_interview_invite_sent ? 'var(--green)' : 'var(--text-muted)' }}>{c.ai_interview_invite_sent ? <Mail size={15} strokeWidth={2} /> : <Circle size={15} strokeWidth={2} />}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: c.ai_interview_invite_sent ? 'var(--green)' : 'var(--text-muted)' }}>
                    {c.ai_interview_invite_sent ? 'AI Interview Invite Sent' : 'AI Interview Invite Pending'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Portal interview link email</div>
                </div>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 8,
                background: c.invite_email_sent ? 'rgba(5,150,105,0.08)' : 'var(--bg-hover)',
                border: `1px solid ${c.invite_email_sent ? 'var(--green)' : 'var(--border)'}`,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', color: c.invite_email_sent ? 'var(--green)' : 'var(--text-muted)' }}>{c.invite_email_sent ? <Mail size={15} strokeWidth={2} /> : <Circle size={15} strokeWidth={2} />}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: c.invite_email_sent ? 'var(--green)' : 'var(--text-muted)' }}>
                    {c.invite_email_sent ? 'Scheduling Invite Sent' : 'Scheduling Invite Pending'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Human interview scheduling email</div>
                </div>
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 8,
                background: c.reminder_24h_sent ? 'rgba(5,150,105,0.08)' : 'var(--bg-hover)',
                border: `1px solid ${c.reminder_24h_sent ? 'var(--green)' : 'var(--border)'}`,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', color: c.reminder_24h_sent ? 'var(--green)' : 'var(--text-muted)' }}>{c.reminder_24h_sent ? <Mail size={15} strokeWidth={2} /> : <Circle size={15} strokeWidth={2} />}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: c.reminder_24h_sent ? 'var(--green)' : 'var(--text-muted)' }}>
                    {c.reminder_24h_sent ? '24h Reminder Sent' : '24h Reminder Pending'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sent 24 hours before interview</div>
                </div>
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 8,
                background: c.reminder_1h_sent ? 'rgba(5,150,105,0.08)' : 'var(--bg-hover)',
                border: `1px solid ${c.reminder_1h_sent ? 'var(--green)' : 'var(--border)'}`,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', color: c.reminder_1h_sent ? 'var(--green)' : 'var(--text-muted)' }}>{c.reminder_1h_sent ? <Mail size={15} strokeWidth={2} /> : <Circle size={15} strokeWidth={2} />}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: c.reminder_1h_sent ? 'var(--green)' : 'var(--text-muted)' }}>
                    {c.reminder_1h_sent ? '1h Reminder Sent' : '1h Reminder Pending'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sent 1 hour before interview</div>
                </div>
              </div>
            </div>

            {/* Send reminder buttons */}
            {mayRecruit && c.interview_status === 'Scheduled' && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase' }}>Send Reminders Manually</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleSendReminder('24h')}
                    disabled={!jobActive || sendingReminder === '24h'}
                    title={jobLockHint}
                  >
                    {sendingReminder === '24h' ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Sending...</> : 'Send 24h Reminder'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleSendReminder('1h')}
                    disabled={!jobActive || sendingReminder === '1h'}
                    title={jobLockHint}
                  >
                    {sendingReminder === '1h' ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Sending...</> : 'Send 1h Reminder'}
                  </button>
                </div>
              </div>
            )}
            {c.interview_status !== 'Scheduled' && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Reminders can only be sent after the interview is scheduled.
              </div>
            )}
          </div>

          {/* Self-schedule portal hidden — candidates join from the meeting email only
          {c.interview_status !== 'Completed' && (
            <div className="card">
              ...
            </div>
          )}
          */}

          {/* Invite email preview */}
          {c.invite_email_content && (
            <div className="card">
              <div className="section-title">Invite Email Content</div>
              <pre style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8,
                padding: '14px 16px', fontSize: 12.5, color: 'var(--text-secondary)',
                whiteSpace: 'pre-wrap', fontFamily: 'inherit', maxHeight: 300, overflowY: 'auto', lineHeight: 1.7,
              }}>{c.invite_email_content}</pre>
            </div>
          )}

          {!c.invite_email_content && !c.invite_email_sent && (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon"><Mail size={40} strokeWidth={1.5} /></div>
                <div className="empty-state-title">No invite sent yet</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  Schedule an interview to generate and send the invite email.
                </div>
                {canScheduleAi && (
                  <button className="btn btn-primary mt-4" onClick={() => setShowSchedule(true)} disabled={!jobActive} title={jobLockHint}>
                    Schedule AI Interview
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Second Round Info */}
          {c.second_round_invited && (
            <div className="card">
              <div className="section-title">Second Round — Human Interview</div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                {[
                  { label: 'Invitation Sent', ok: c.second_round_email_sent, sub: 'Second round invite' },
                  { label: c.second_round_slot ? 'Slot Confirmed' : 'Awaiting Schedule', ok: !!c.second_round_slot, sub: c.second_round_slot || 'Not yet scheduled' },
                ].map(({ label, ok, sub }) => (
                  <div key={label} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 14px', borderRadius: 8,
                    background: ok ? 'rgba(5,150,105,0.08)' : 'var(--bg-hover)',
                    border: `1px solid ${ok ? 'var(--green)' : 'var(--border)'}`,
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', color: ok ? 'var(--green)' : 'var(--text-muted)' }}>{ok ? <Check size={15} strokeWidth={2.5} /> : <Circle size={15} strokeWidth={2} />}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: ok ? 'var(--green)' : 'var(--text-muted)' }}>{label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              {c.second_round_meeting_link && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>Meeting Link</div>
                  <a href={c.second_round_meeting_link} target="_blank" rel="noreferrer"
                    style={{ fontSize: 13, color: 'var(--accent)', wordBreak: 'break-all' }}>
                    {c.second_round_meeting_link}
                  </a>
                </div>
              )}
              {c.second_round_location && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>Location</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {c.second_round_location}
                  </div>
                </div>
              )}
              {canScheduleSecondRound && (c.pipeline_stage === 'second_round_invited' || c.pipeline_stage === 'interview_completed') && (
                <button className="btn btn-primary" onClick={() => setShowSecondRound(true)} disabled={!jobActive} title={jobLockHint} style={{ marginTop: 8 }}>
                  <CalendarClock size={14} strokeWidth={2} /> Schedule Second Round Now
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── RECOMMENDATION ─── */}
      {tab === 'shortlist' && c.final_recommendation && (
        <div className="card">
          <div className="section-title">AI Final Recommendation</div>
          <div style={{ marginBottom: 16 }}>
            <span className={`badge ${c.final_recommendation.overall_recommendation === 'Recommended' ? 'badge-green' : 'badge-yellow'}`}>
              {c.final_recommendation.overall_recommendation}
            </span>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>{c.final_recommendation.executive_summary}</p>
          <div className="grid-2">
            <div>
              <div className="section-title">Top Strengths</div>
              {(c.final_recommendation.top_3_strengths || []).map((s, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>+ {s}</div>
              ))}
            </div>
            <div>
              <div className="section-title">Areas to Probe</div>
              {(c.final_recommendation.suggested_focus_areas_for_technical_round || []).map((s, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '5px 0', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}><ArrowRight size={12} strokeWidth={2} style={{ flexShrink: 0 }} /> {s}</div>
              ))}
            </div>
          </div>
        </div>
      )}
      {tab === 'shortlist' && !c.final_recommendation && (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon"><ListChecks size={40} strokeWidth={1.5} /></div>
            <div className="empty-state-title">No recommendation yet</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Complete the interview and generate the shortlist to see the AI recommendation.</div>
          </div>
        </div>
      )}
    </div>
  );
}
